import { Box } from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  ResponseOfAttribute,
  SaveAttributeRequest,
  SaveResponse,
} from "services/KnowfullyAdminClient";
import attributeStore from "stores/attributeStore";
import translateError from "utils/translateError";
import { AttributeForm } from "./AttributeForm";

export function AttributeUpdateForm() {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const { attributeService, attributeDetailsUrl } = useGlobalContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const storedOrganizationAttributeType = attributeStore.useState(
    (s) => s.organizationAttributeType,
  );

  const backToDetailsUrl = attributeDetailsUrl(id);

  const onCancel = () => {
    navigate(backToDetailsUrl);
  };

  const attributeResult = useStrictQuery<ResponseOfAttribute>(
    ["attribute", id],
    () => attributeService.getAttributeById(id!),
    {
      enabled: !!id,
    },
  );

  const attribute = attributeResult.data?.data;
  const organizationAttributeType =
    storedOrganizationAttributeType ?? attribute?.organizationAttributeType;
  useTitle(attribute?.value);

  const onSubmit = async (values: SaveAttributeRequest) => {
    try {
      const updatedAttribute: SaveResponse = await registerPromise(
        attributeService.saveAttribute(
          SaveAttributeRequest.fromJS({
            id,
            attributeTypeId: organizationAttributeType?.attributeTypeId,
            organizationAttributeTypeId: organizationAttributeType?.id,
            displayText: values.displayText || values.value,
            ...values,
          }),
        ),
      );

      queryClient.invalidateQueries(["getAttributes"]);
      queryClient.invalidateQueries(["skuAttributesByType"]);
      queryClient.invalidateQueries(["attributes"]);
      queryClient.invalidateQueries(["attribute", id]);

      navigate(attributeDetailsUrl(id));

      return updatedAttribute;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  if (!id) return <Navigate to={backToDetailsUrl} replace />;
  if (attributeResult.isLoading) return <Loader />;

  return (
    <>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={backToDetailsUrl}
          labelKey="backToDetailsButtonLabel"
        />
      </Box>
      <AttributeForm
        onSubmit={onSubmit}
        organizationAttributeType={organizationAttributeType}
        attribute={attribute}
        submitLabel={t(
          { id: "editAttributeLabel" },
          { attributeType: organizationAttributeType?.attributeType?.name },
        )}
        onCancel={onCancel}
      />
    </>
  );
}
